type Obj = Record<string, unknown>;
const object = (value: unknown): Obj =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Obj)
    : {};
const list = (value: unknown): Obj[] =>
  Array.isArray(value) ? value.map(object) : [];
const text = (value: unknown): string =>
  typeof value === 'string' ? value : '';

export const safeSourceUrl = (value: unknown): string | undefined => {
  try {
    const url = new URL(text(value));
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
};

export function dowJonesEvidence(value: unknown) {
  const data = object(value);
  const match = object(data.match);
  const attrs = object(object(data.profile).attributes);
  const person = object(attrs.person);
  const watchlist = object(attrs.watchlist);
  const matchedHints = [
    ...list(match.iconHints),
    ...list(object(match.attributes).iconHints),
  ].map((hint) => text(hint.iconHint));
  const hints = [
    ...matchedHints,
    ...list(person.iconHints),
    ...list(object(attrs.entity).iconHints),
  ].map((hint) => (typeof hint === 'string' ? hint : text(hint.iconHint)));
  const isSip = hints.some((hint) =>
    ['SI-PERSON', 'SI-INV-PERSON'].includes(hint)
  );
  const isOol = hints.some((hint) =>
    ['OOL', 'OOL-PERSON', 'OOL-ENTITY'].includes(hint)
  );
  const dateText = (value: unknown) => {
    const date = object(value);
    return [date.year, date.month, date.day]
      .filter((part) => typeof part === 'number' || typeof part === 'string')
      .join('-');
  };
  const listReferences = object(attrs.listReference);
  const officialLists = list(listReferences.otherOfficialLists).map((item) => ({
    name: text(item.name) || 'Other official list',
    description: text(item.description),
    fromDate: dateText(item.fromDate),
    toDate: dateText(item.toDate),
    sources: Array.isArray(item.sources)
      ? item.sources
          .filter((source): source is string => typeof source === 'string')
          .map((source) => ({ title: source, url: safeSourceUrl(source) }))
      : [],
  }));
  const categories: string[] = [];
  const collectCategories = (nodes: unknown) => {
    for (const node of list(nodes)) {
      const description = text(node.description);
      if (description) categories.push(description);
      collectCategories(node.subContentTypes);
    }
  };
  collectCategories(object(watchlist.contentTypeDetails).contentTypes);
  const comments = object(watchlist.commentDetails);
  const allegations = list(watchlist.reportedAllegationDetails).map((item) => {
    const rows = list(item.caseRowDetails);
    const descriptions = [text(item.governmentAction)];
    for (const row of rows) {
      descriptions.push(
        text(row.parentContentType),
        text(row.contentType),
        text(row.governmentAction)
      );
    }
    const sources = [
      ...list(item.reportedAllegationSourceDetails),
      ...rows.flatMap((row) => list(row.reportedAllegationSourceDetails)),
    ].map((source) => {
      const date = object(source.date);
      return {
        name: text(source.name) || text(source.value),
        snippet: text(source.snippet),
        date: [date.year, date.month, date.day]
          .filter(
            (part): part is number | string =>
              typeof part === 'number' || typeof part === 'string'
          )
          .join('-'),
        reference: text(source.accessionNumber) || text(source.drn),
      };
    });
    return {
      descriptions: [...new Set(descriptions.filter(Boolean))],
      sources,
    };
  });
  return {
    isSip,
    isOol,
    isSanction: matchedHints.some((hint) =>
      ['SAN', 'SAN-PERSON', 'SAN-ENTITY'].includes(hint)
    ),
    isPep: matchedHints.some((hint) => ['PEP', 'RCA'].includes(hint)),
    otherCategoriesAutoDismissed: data.otherCategoriesAutoDismissed === true,
    mediaIdentityAutoDismissed: data.mediaIdentityAutoDismissed === true,
    mediaReviewPaused: data.mediaReviewPaused === true,
    officialLists,
    listNotice: text(listReferences.contentNotice),
    categories: [...new Set(categories)],
    comments: [text(comments.siComment), text(comments.siInvComment)].filter(
      Boolean
    ),
    notice: text(watchlist.contentNotice),
    allegations,
    sources: list(watchlist.sources).map((source) => ({
      title:
        text(source.title) ||
        text(source.drn) ||
        text(source.accessionNumber) ||
        'Source',
      url: safeSourceUrl(source.externalLink),
    })),
  };
}

export function dowJonesMatchLabel(value: unknown, status: string): string {
  const evidence = dowJonesEvidence(value);
  if (evidence.mediaReviewPaused) {
    if (
      status === 'SANCTION_MATCH' ||
      (evidence.isSanction && !evidence.otherCategoriesAutoDismissed)
    )
      return 'Sanctions';
    if (
      ['PEP_MATCH', 'RCA_MATCH'].includes(status) ||
      (evidence.isPep && !evidence.otherCategoriesAutoDismissed)
    )
      return 'PEP / RCA';
    return 'Paused OOL/SIP';
  }
  return evidence.isSip
    ? 'Adverse Media (SIP)'
    : evidence.isOol
    ? 'Other Official Lists (OOL)'
    : '';
}
